import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/grain";
import { initImageFade } from "./lib/image-fade";
import { markAppMounted } from "./lib/app-mounted";

// createRoot (not hydrateRoot): the prerender shim is a simplified DOM
// approximation, not true SSR output. hydrateRoot hit recoverable mismatches
// on every mount, blocking the main thread ~2s on mobile.
//
// CRITICAL: the entry module runs right after HTML parse but BEFORE the
// browser's first paint. Calling createRoot() synchronously here wiped the
// prerender shim before it ever painted, so FCP/LCP waited for React's
// entire first render (~2.3s under 4x CPU throttle on mobile) — Lighthouse
// filmstrips showed a white screen until then. (The old comment claimed
// "LCP already fired on the pre-painted poster"; the filmstrip proved it
// did not — the shim never painted.)
//
// Fix: yield so the browser paints the static shim FIRST (instant FCP/LCP
// on the prerendered hero), then mount React one frame later to take over.
// The shim mirrors the React hero, so the swap is visually seamless.
const mountReact = () => {
  createRoot(document.getElementById("root")!).render(<App />);
  // One frame after the initial (synchronous) render, flag the app as mounted
  // so any hero that mounts on a LATER client navigation animates its entrance,
  // while the first paint over the prerender shim stays animation-free (seamless
  // swap). The initial hero's useState initializer has already run by now and
  // captured `false`.
  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => markAppMounted());
  } else {
    markAppMounted();
  }
};

if (typeof requestAnimationFrame !== "undefined") {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => setTimeout(mountReact, 0)),
  );
} else {
  mountReact();
}

// Image fade wiring is not LCP-critical — defer until after first paint.
if (typeof window !== "undefined") {
  const startFade = () => initImageFade();
  if (typeof (window as Window & { requestIdleCallback?: unknown }).requestIdleCallback === "function") {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback(startFade, { timeout: 3000 });
  } else {
    window.addEventListener("load", startFade, { once: true });
  }
}

type IdleWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };

const scheduleIdle = (callback: () => void) => {
  const idleWindow = window as IdleWindow;
  if (idleWindow.requestIdleCallback) {
    const id = idleWindow.requestIdleCallback(callback, { timeout: 5000 });
    return () => idleWindow.cancelIdleCallback?.(id);
  }

  const id = window.setTimeout(callback, 2500);
  return () => window.clearTimeout(id);
};

const toSameOriginPath = (value: string | null) => {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.href);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}` : null;
  } catch {
    return null;
  }
};

const collectWarmCacheUrls = () => {
  const urls = new Set<string>();
  const nodes = document.querySelectorAll<HTMLLinkElement | HTMLScriptElement>(
    'script[type="module"][src], link[rel="modulepreload"][href], link[rel="stylesheet"][href], link[rel="preload"][href]',
  );

  nodes.forEach((node) => {
    if (node instanceof HTMLLinkElement && node.rel === "preload") {
      const media = node.media;
      if (media && !window.matchMedia(media).matches) return;
    }

    const url = toSameOriginPath(node.getAttribute("href") ?? node.getAttribute("src"));
    if (url) urls.add(url);
  });

  return [...urls];
};

// Register lightweight service worker for static asset caching (prod only).
// After load, warm the current page's already-discovered critical assets so
// return visits can paint from Cache Storage without adding first-paint work.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((registration) => {
      scheduleIdle(() => {
        const urls = collectWarmCacheUrls();
        if (!urls.length) return;

        navigator.serviceWorker.ready.then((readyRegistration) => {
          const worker =
            readyRegistration.active ||
            registration.active ||
            registration.waiting ||
            registration.installing;
          worker?.postMessage({ type: "MANE_WARM_URLS", urls });
        });
      });
    }).catch(() => {});
  });
}
